import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Trash2, Type, Mail, List, ToggleLeft, Calendar, Plus, ChevronDown } from "lucide-react";
import { useState } from "react";
import { FormElement, FormElementType, DayOfWeek } from "../types";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { fieldTypes, getFieldIcon } from "./formElementUtils";

interface SortableFormElementProps {
  element: FormElement;
  index: number;
  onUpdate: (updates: Partial<FormElement>) => void;
  onRemove: () => void;
}

export const SortableFormElement = ({ element, index, onUpdate, onRemove }: SortableFormElementProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: element.id });
  const [isOpen, setIsOpen] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className="group border rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow duration-200"
    >
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="p-3 flex items-center justify-between border-b">
          <div className="flex items-center gap-2">
             <button 
              {...attributes} 
              {...listeners} 
              className="cursor-grab hover:text-primary focus:outline-none"
              aria-label="Húzd a rendezéshez"
            >
              <span className="w-5 h-5 text-gray-400 group-hover:text-gray-600">⋮⋮</span>
            </button>
            <div className="flex items-center gap-2">
              {getFieldIcon(element.type)}
              <Badge variant="secondary" className="text-sm font-medium">
                Mező {index + 1}
              </Badge>
              <span className="text-sm text-[#8E9196]">{element.title || 'Névtelen mező'}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onRemove}
                    className="text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Mező törlése</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm">
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
          </div>
        </div>

        <CollapsibleContent>
          <div className="p-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor={`type-${element.id}`} className="text-sm font-medium">
                Mező típusa
              </Label>
              <Select
                value={element.type}
                onValueChange={(value: FormElementType) => onUpdate({ type: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Válassz mező típust" />
                </SelectTrigger>
                <SelectContent>
                  {fieldTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className="w-4 h-4" />
                        <span>{type.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`title-${element.id}`} className="text-sm font-medium">
                Mező címe
              </Label>
              <Input
                id={`title-${element.id}`}
                value={element.title}
                onChange={(e) => onUpdate({ title: e.target.value })}
                placeholder="Add meg a mező címét"
                className="w-full"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id={`required-${element.id}`}
                checked={element.required}
                onCheckedChange={(checked) => onUpdate({ required: checked })}
              />
              <Label htmlFor={`required-${element.id}`} className="text-sm font-medium">
                Kötelező mező
              </Label>
            </div>

            {element.type === "choice" && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Opciók</Label>
                <div className="space-y-2">
                  {element.options?.map((option, optionIndex) => (
                    <div key={optionIndex} className="flex items-center gap-2">
                      <Input
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...(element.options || [])];
                          newOptions[optionIndex] = e.target.value;
                          onUpdate({ options: newOptions });
                        }}
                        placeholder={`${optionIndex + 1}. opció`}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          const newOptions = element.options?.filter((_, i) => i !== optionIndex);
                          onUpdate({ options: newOptions });
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newOptions = [...(element.options || []), ''];
                      onUpdate({ options: newOptions });
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Opció hozzáadása
                  </Button>
                </div>
                <div className="flex items-center space-x-2 mt-2">
                  <Switch
                    id={`multiple-${element.id}`}
                    checked={element.isMultiple}
                    onCheckedChange={(checked) => onUpdate({ isMultiple: checked })}
                  />
                  <Label htmlFor={`multiple-${element.id}`} className="text-sm font-medium">
                    Több választás engedélyezése
                  </Label>
                </div>
              </div>
            )}

            {element.type === "date" && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Elérhető napok</Label>
                  <ToggleGroup 
                    type="multiple"
                    value={element.dateConfig?.availableDays || []}
                    onValueChange={(values: DayOfWeek[]) => 
                      onUpdate({
                        dateConfig: {
                          ...element.dateConfig,
                          availableDays: values,
                        }
                      })
                    }
                    className="justify-start"
                  >
                    {[
                      { id: 'monday' as DayOfWeek, label: 'H' },
                      { id: 'tuesday' as DayOfWeek, label: 'K' },
                      { id: 'wednesday' as DayOfWeek, label: 'Sze' },
                      { id: 'thursday' as DayOfWeek, label: 'Cs' },
                      { id: 'friday' as DayOfWeek, label: 'P' },
                      { id: 'saturday' as DayOfWeek, label: 'Szo' },
                      { id: 'sunday' as DayOfWeek, label: 'V' }
                    ].map((day) => (
                      <ToggleGroupItem
                        key={day.id}
                        value={day.id}
                        aria-label={`${day.label} ki/be kapcsolása`}
                        className="capitalize"
                      >
                        {day.label}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Időpontok</Label>
                  {element.dateConfig?.timeSlots.map((slot, slotIndex) => (
                    <div key={slotIndex} className="space-y-4 p-4 border rounded-lg">
                      <div className="flex items-center gap-2">
                        <Input
                          type="time"
                          value={slot.startTime}
                          onChange={(e) => {
                            const newSlots = [...element.dateConfig!.timeSlots];
                            newSlots[slotIndex] = { ...slot, startTime: e.target.value };
                            onUpdate({
                              dateConfig: {
                                ...element.dateConfig,
                                timeSlots: newSlots,
                              }
                            });
                          }}
                          className="w-28"
                        />
                        <span>-</span>
                        <Input
                          type="time"
                          value={slot.endTime}
                          onChange={(e) => {
                            const newSlots = [...element.dateConfig!.timeSlots];
                            newSlots[slotIndex] = { ...slot, endTime: e.target.value };
                            onUpdate({
                              dateConfig: {
                                ...element.dateConfig,
                                timeSlots: newSlots,
                              }
                            });
                          }}
                          className="w-28"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const newSlots = element.dateConfig!.timeSlots.filter((_, i) => i !== slotIndex);
                            onUpdate({
                              dateConfig: {
                                ...element.dateConfig,
                                timeSlots: newSlots,
                              }
                            });
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newSlots = [
                        ...element.dateConfig!.timeSlots, 
                        { 
                          startTime: "09:00", 
                          endTime: "17:00"
                        }
                      ];
                      onUpdate({
                        dateConfig: {
                          ...element.dateConfig,
                          timeSlots: newSlots,
                        }
                      });
                    }}
                    className="mt-2"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Időpont hozzáadása
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
